/* eslint-disable complexity */
import './OfferPhoneModalMobile.css';

import React from 'react';

import classname from '@search/classname/src';
import { HouseCategoryEnum } from '@search/graphql-typings';
import { Modal, Button, Typography } from '@vtblife/uikit';
import { Size, Spinner } from '@vtblife/uikit/legacy';
import Icon from '@vtblife/uikit-icons';

import { Currency } from '@search/filter-enums/enums/Currency';
import { OfferType } from '../../../../domain/offer/search/page/OfferQuery';
import { dealTypePeriodCompat, OfferSnippetModel } from '../../../../domain/offer/search/page/SearchOffersQuery';
import { ImageLazySeo } from '../../../../domain/image/lazy/ImageLazySeo';
import { CallToActionModal } from '../CallToActionButton';
import { ReportModal } from '../ReportButton/ReportModal';
import { PriceWithoutPopper } from '../Price/PriceWithoutPopper';
import { OnlinePurchase2RequestModal } from '../promo-service-banner/PromoOnlinePurchase2/OnlinePurchase2RequestModal';
import { useOfferPhoneModal } from './useOfferPhoneModal';

const cn = classname.bind(null, 'OfferPhoneModalMobile');

export function OfferPhoneModalMobile({
    offer: offerCard,
    snippet,
    offerId = offerCard?.id ?? snippet?.id,
    onClose
}: {
    offerId?: string;
    phoneHref: string;
    phoneFormatted: string;
    isDoppelganger: boolean;
    onClose(): void;
    offer?: OfferType;
    snippet?: OfferSnippetModel;
}) {
    const {
        showBuyOnline,
        handleCallback,
        handleNoCall,
        isReporting,
        handleReport,
        handleSuccess,
        seller,
        receivedOffer,
        newBuildingEmailExtra,
        status,
        price,
        isBuyButton,
        isNewBuilding,
        error,
        offerUrl,
        realtyObject
    } = useOfferPhoneModal({ offerId, offer: offerCard, snippet, isMobile: true });

    if (status === 'online-purchase' && receivedOffer) {
        return (
            // в карточке window.location.href будет верный, а в выдачах/похожих - нет
            <OnlinePurchase2RequestModal
                offerUrl={offerCard ? undefined : offerUrl}
                onClose={onClose}
                isMobile
            />
        );
    }

    if (status === 'report' && receivedOffer) {
        return (<ReportModal
            isMobile
            offerId={receivedOffer.id}
            handleCloseModal={onClose}
        />);
    }

    if (status === 'call-to-action' && receivedOffer && seller) {
        return (<CallToActionModal
            isMobile
            newBuildingCard={receivedOffer.newBuildingCard}
            offerUrl={offerUrl}
            offerStatus={receivedOffer.status}
            onClose={onClose}
            offerLocation={receivedOffer.location}
            newBuildingEmailExtra={newBuildingEmailExtra}
            seller={seller}
            gtmSendClick='cl_showtel_modal_info_verif_silent_callback_sent'
            isNewBuilding={isNewBuilding}
        />);
    }

    const descriptionEnabled = receivedOffer?.dealType?.__typename === 'ProfOfferDealSell' && (
        realtyObject?.__typename === 'ProfOfferFlat' ||
        realtyObject?.__typename === 'ProfOfferRoom' ||
        realtyObject?.__typename === 'ProfOfferLand' ||
        (
            realtyObject?.__typename === 'ProfOfferHouse' && (
                realtyObject.category2?.value === HouseCategoryEnum.Standalone ||
                realtyObject.category2?.value === HouseCategoryEnum.Townhouse
            )
        )
    );

    return (
        <Modal
            opened
            size='s'
            swipeToClose={false}
            onClose={onClose}
        >
            <div className={cn('quest')}>
                {status === 'success' && (<>
                    <Message
                        icon={<Icon size='m' name='thumb-up' color='#78BC29' />}
                    >
                        Спасибо за ваш отзыв!
                    </Message>
                </>)}

                {status === 'ask-callback' && (<>
                    <Message>
                        Хотите чтобы вам перезвонили?
                    </Message>
                    <div className={cn('buttons', { withoutGrid: true })}>
                        <Button
                            size='xs'
                            variant='secondary'
                            dataTest='callback'
                            onClick={handleCallback}
                        >Да, перезвоните мне</Button>
                    </div>
                </>)}

                {error && (<>
                    <Message>
                        Что-то пошло не так
                    </Message>
                    <pre>{JSON.stringify(error, null, ' ')}</pre>
                </>)}

                {status === 'start' && (<>
                    <Message>
                        После звонка укажите подтвердилась&nbsp;ли информация, указанная в&nbsp;объявлении
                    </Message>

                    <div className={cn('buttons')}>
                        <Button size='xs' variant='secondary' dataTest='success'
                            onClick={handleSuccess}>Да</Button>
                        <Button size='xs' variant='secondary' dataTest='report'
                            onClick={handleReport}>Нет</Button>
                        <div className={cn('wide-button')}>
                            <Button
                                size='xs'
                                fullWidth
                                variant='secondary'
                                dataTest='no-call'
                                loading={isReporting}
                                onClick={handleNoCall}
                            >Не взяли трубку</Button>
                        </div>
                    </div>
                </>)}
            </div>

            {! receivedOffer ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px'
                }}>
                    <Spinner size={Size.Large} />
                </div>
            ) : null}
            {descriptionEnabled && receivedOffer && (
                <div className={cn('offer')}>
                    <div className={cn('card')}>
                        {receivedOffer.gallery?.mainImage && (
                            <div className={cn('img-wrap')}><ImageLazySeo
                                originPath={receivedOffer.gallery.mainImage.originPath}
                                width={60}
                                height={60}
                            /></div>
                        )}

                        <div className={'description'}>
                            <Typography
                                variant='primary'
                                bold
                            >
                                {receivedOffer.text?.titleSnippet}
                            </Typography>
                            {price ? <div className={cn('price')}>
                                <div className={cn('main-price')}>
                                    <PriceWithoutPopper
                                        bold={false}
                                        history={price.history ?? []}
                                        variant='secondary-alone'
                                        priceValue={price.rubPrice ?? price.value}
                                        priceCurrency={price.rubPrice ? Currency.RUB : price.currency}
                                        rentPeriod={dealTypePeriodCompat(receivedOffer.dealType)}
                                        hasHighlight={receivedOffer.status?.hasHighlight}
                                    />
                                    {price.rubPrice && (
                                        <div className={cn('original-price')}>
                                            <PriceWithoutPopper
                                                bold={false}
                                                variant='secondary-alone'
                                                priceValue={price.value}
                                                priceCurrency={price.currency}
                                                rentPeriod={dealTypePeriodCompat(receivedOffer.dealType)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div> : null}
                        </div>
                    </div>

                    {isBuyButton && (
                        <Typography variant='secondary-alone'>
                            Безопасная сделка через&nbsp;М2, <a className={cn('link')} href='/services/deal' target='_blank'>подробнее</a>
                        </Typography>
                    )}

                    {isBuyButton && (
                        <div className={cn('offer-buttons')}>
                            <Button
                                size='s'
                                fullWidth
                                variant='brand-purple'
                                dataTest='buy-online'
                                onClick={showBuyOnline}
                            >Сделка</Button>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}

function Message({ children, icon }: {
    icon?: React.ReactNode;
    containerRef?: React.RefCallback<HTMLElement>;
    children: React.ReactNode;
}) {
    return (
        <div className={cn('message')}>
            {icon && (
                <div className={cn('message-icon')}>
                    {icon}
                </div>
            )}
            <Typography className={cn('message-text')} variant={icon ? 'h4' : 'secondary-alone'}>
                {children}
            </Typography>
        </div>
    );
}
